import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { boardPath, boardSchema } from '@molt/shared';
import { CardSurface, Notice, Skeleton } from '@/components';
import { get, isUnauthenticated } from '@/lib/api';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { useAcceptBoardInvite } from '@/lib/invitations';
import { signInHref, useSession } from '@/lib/session';

export const Route = createFileRoute('/board-invites/$token')({
  component: BoardInviteRoute,
});

function BoardInviteRoute() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const session = useSession();
  const accept = useAcceptBoardInvite(token);
  const attemptedTokenRef = useRef<string | null>(null);
  const [redirectError, setRedirectError] = useState<string | null>(null);
  const { t } = useInterfaceLanguage();

  useEffect(() => {
    if (session.isPending) return;
    if (session.isSignedOut) {
      void navigate({ href: signInHref(window.location), replace: true });
      return;
    }
    if (session.profile === null || attemptedTokenRef.current === token) return;

    attemptedTokenRef.current = token;
    setRedirectError(null);
    void redeemInvite();

    async function redeemInvite() {
      try {
        const result = await accept.mutateAsync();
        const board = await get(
          `/boards/${encodeURIComponent(result.boardId)}`,
          boardSchema,
        );
        await navigate({ href: boardPath(board.shortLink, board.slug), replace: true });
      } catch (thrown) {
        if (isUnauthenticated(thrown)) {
          void navigate({ href: signInHref(window.location), replace: true });
          return;
        }
        /* ⚠ `thrown.message` carries TWO provenances, and only one of them is a
           response body. Both stay unrouted here, for two DIFFERENT reasons.

           SERVER-AUTHORED: `apps/server/src/routes/board-invite-links.ts` writes
           the three invite refusals; `apiErrorSchema.safeParse` in
           `apps/web/src/lib/api.ts` lifts that sentence out of the body and
           rethrows it as an `ApiError`. A catalogue cannot translate a response
           body — reported as a contract bug, not routed.

           CLIENT-AUTHORED: every other throw reaching this catch is composed in
           English by `apps/web/src/lib/api.ts` itself — `'Could not reach the
           server'` (:97), `` `Unexpected ${response.status} response` `` (:116)
           and `` `The server sent an unexpected shape for ${path}` `` (:121).
           These ARE routable copy and this slice does not route them, because
           `lib/api.ts` is outside its file list; reported as a residual, with
           the ownership note that the first of the three is ALREADY routed as
           `signedOut.error.network` for `lib/auth.ts`'s own network arm — so the
           identical sentence is translated on the sign-in surface and
           untranslated on this one. Matching it here on its English bytes would
           be a second defect, not a fix; the repair belongs in `lib/api.ts`.

           ⚠ The routed arm below is close to unreachable. `ApiError extends
           Error` (`packages/shared/src/errors.ts`), so `thrown instanceof Error`
           holds for everything `api.ts` throws and `thrown.message` always wins;
           `signedOut.invite.genericError` renders only if a non-`Error` is
           thrown. The live channel is the unrouted one. */
        setRedirectError(
          thrown instanceof Error ? thrown.message : t('signedOut.invite.genericError'),
        );
      }
    }
  }, [
    accept,
    navigate,
    session.isPending,
    session.isSignedOut,
    session.profile,
    t,
    token,
  ]);

  if (session.isPending || accept.isPending || accept.isIdle) {
    return (
      <CardSurface
        variant="sheet"
        className="mx-auto flex w-full max-w-sm flex-col gap-4 p-6"
      >
        <Skeleton shape="line" width="10rem" />
        <Skeleton shape="row" />
      </CardSurface>
    );
  }

  // `accept.error.message` is the SECOND path into the same channel the catch
  // above documents — same two provenances, same reasons for staying unrouted.
  const errorMessage =
    redirectError ??
    (accept.error !== null && !isUnauthenticated(accept.error)
      ? accept.error.message
      : null);

  if (errorMessage !== null) {
    return (
      <CardSurface
        variant="sheet"
        className="mx-auto flex w-full max-w-sm flex-col gap-4 p-6"
      >
        <h1 className="font-display text-lg font-semibold text-ink">
          {t('signedOut.invite.title')}
        </h1>
        <Notice tone="bad" body={errorMessage} />
      </CardSurface>
    );
  }

  return (
    <CardSurface
      variant="sheet"
      className="mx-auto flex w-full max-w-sm flex-col gap-4 p-6"
    >
      <Skeleton shape="line" width="10rem" />
      <Skeleton shape="row" />
    </CardSurface>
  );
}
